import { RESOLVING_STATUS_ANY } from 'core/store/modules/common/literals';
import {
  INDEX_DESIGN_PARENT,
  INDEX_DESIGN_ROOT,
  SORT_AND_USE_INDEX,
} from 'core/modules/server/constants';
import dbname from 'core/repositories/utils/dbname';
import regexpSwitcher from 'core/repositories/utils/regexp-switcher';
import type { SearchState } from 'core/types';
import { env } from 'core/utils';
import PouchDB from 'pouchdb';
import PouchdbFind from 'pouchdb-find';
import type { UUID} from 'short-uuid';
import { uuid } from 'short-uuid';

import type { IRead, IWrite } from './interfaces';
import getReduceConflicts from './utils/get-reduce-conflicts';

PouchDB.plugin(PouchdbFind);

type DBDatabase = PouchDB.Database;

export interface IServer<T> extends IRead<T>, IWrite<T> {
  fetch(...argument: any[]): Promise<T>[] | any;
  flush(documents: any[]): Promise<T>[] | any;
}
export class PouchDBRepository implements IServer<DBDatabase> {
  protected instance: any;
  constructor(name: string) {
    this.instance = new PouchDB(dbname(name));

    const config = {
      since: 'now',
      live: true,
      include_docs: true,
    };

    this.instance.changes(config);

    // for debugging it should be removed after all
    this.instance.on('change', ({ doc }: any) => {
      // eslint-disable-next-line no-console
      console.log('listening-document', doc);
    });

    if (env.NODE_ENV === 'development') {
      this.instance.replicate
        .to(`${env.POUCHDB_HOST}:${env.POUCHDB_PORT}/staging${name}`, {
          live: true,
        })
        .on('change', console.log)
        .on('paused', console.log)
        .on('error', console.log);
    }
  }

  get = (id: UUID) => this.instance.get(id);

  getChildren = (parent: UUID): Promise<Promise<DBDatabase>[]> =>
    this.fetch(
      {
        parent: { $eq: parent },
      },
      { fields: ['parent'], index: INDEX_DESIGN_PARENT },
    );

  getAll = (root: UUID | null = null, conditions: any = {}): Promise<Promise<DBDatabase>[]> =>
    this.fetch({
      parent: { $gt: null },
      order: { $gt: null },
      root: { $eq: root },
      ...conditions,
    });

  fetch = async (
    selector: any = {},
    optSortAndUseIndex: any = SORT_AND_USE_INDEX,
    additionalOptions: any = {},
  ): Promise<Promise<DBDatabase>[]> => {
    const { fields, index } = optSortAndUseIndex;

    const options = {
      selector,
      sort: fields,
      use_index: index,
      ...additionalOptions,
    };

    if (fields && index) {
      await this.instance.createIndex({
        index: {
          fields,
          ddoc: index,
        },
      });
    }

    const { docs } = await this.instance.find(options);

    return docs;
  };

  flush = async (documents: any[]) => {
    const results = await this.instance.bulkDocs(documents);
    const conflicts: any[] = [];
    const history: any = {};

    results.forEach((result: any) => {
      const { id } = result;

      if (result.ok) {
        history[id] = result.rev;
      } else if (result.status === 409) {
        conflicts.push(id);
      } else {
        throw new Error(result);
      }
    });

    const oh = conflicts.length;
    const ok = results.length - oh;

    if (ok) {
      // eslint-disable-next-line no-console
      console.log(`Successfully synced ${ok} items`);
    }

    if (oh) {
      // eslint-disable-next-line no-console
      console.log(`Ran into ${oh} conflicts`);

      const { rows: conflictRows } = await this.instance.allDocs({
        include_docs: true,
        conflicts: true,
        keys: conflicts,
      });
      const rows = getReduceConflicts(conflictRows);

      // eslint-disable-next-line no-console
      // console.log('conflicts', JSON.stringify(rows, null, 2));

      return { conflicts: rows, history };
    }

    return { conflicts: null, history, documents };
  };

  batch = async (items: any[]): Promise<any> => this.flush(items);

  create = async (item: any): Promise<any> =>
    this.flush([
      {
        _id: uuid(),
        order: 0,
        ...item,
      },
    ]);

  update = async (item: any): Promise<any> =>
    this.flush([
      {
        _id: uuid(),
        ...item,
      },
    ]);

  remove = async (id: string): Promise<any> => {
    const document = await this.instance.get(id);

    return this.flush([
      {
        ...document,
        _id: id,
        _deleted: true,
      },
    ]);
  };

  find = async (root: UUID, search: SearchState, assignments: any[]): Promise<any> => {
    const { inHtml, inTitle, responsible, responsibleStatus, text } = search;

    const conditions: any[] = [];

    const regex = regexpSwitcher(text);

    if (text) {
      if (inTitle) {
        conditions.push({ title: { $regex: regex } });
      }

      if (inHtml) {
        conditions.push({ html: { $regex: regex } });
      }
    }

    const assignmentsCondition =
      responsible || responsibleStatus !== RESOLVING_STATUS_ANY.ANY
        ? {
            _id: {
              $in: assignments.map(({ refId }: any) => refId),
            },
          }
        : {};

    const textConditions = conditions.length ? { $or: conditions } : {};

    let result: any[] = [];

    try {
      result = await this.fetch(
        {
          root: { $eq: root },
          ...assignmentsCondition,
          ...textConditions,
        },
        { fields: ['root'], index: INDEX_DESIGN_ROOT },
        { fields: ['_id'] },
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }

    return result;
  };
}
